import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/Button';

interface Props {
  children: ReactNode;
  /** Optional compact rendering for boundaries wrapping a single page. */
  title?: string;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time errors so one broken widget or a failed lazy chunk
 * does not blank the entire application.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  private reload = () => window.location.reload();

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // A stale deployment can make a lazily-loaded chunk 404; reloading fixes it.
    const isChunkError = /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(error.message);

    return (
      <div className="flex items-center justify-center min-h-[50vh] p-6">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-danger/10 text-danger flex items-center justify-center">
            <AlertTriangle size={22} />
          </div>
          <h2 className="text-lg font-semibold">{this.props.title ?? (isChunkError ? 'A new version is available' : 'Something went wrong')}</h2>
          <p className="text-dark-muted text-sm break-words">
            {isChunkError ? 'The app was updated while you were using it. Reload to get the latest version.' : error.message}
          </p>
          <div className="flex justify-center gap-2">
            {!isChunkError && (
              <Button variant="secondary" onClick={this.reset}>
                Try again
              </Button>
            )}
            <Button onClick={this.reload}>
              <RefreshCw size={16} /> Reload
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
